import axios from 'axios';
import { plainToClass } from 'class-transformer';
import "reflect-metadata";
import { Challenge } from "common/Challenge";
import { Person } from 'models/Person';
import { Invoice } from 'models/Invoice';
import { Service } from 'models/Service';
import { Project, ProjectContract, ProjectHourEntry, ProjectHours } from 'models/Project';
import type { WebPushSubscriptionPayload } from "./Api";
import type { AccountAttachment, AccountAttachmentFormContext } from "models/Account";
import { Account } from "models/Account";
import type { LocaleCatalogue } from "models/Locale";

export interface AccountAsset {
    id: string;
    name: string;
    type: string;
    status: string;
    location?: string;
    serial_number?: string;
    manufacturer?: string;
    model?: string;
    description?: string;
    created_at: string;
    updated_at: string;
}

export interface AccountAssetNote {
    id: string;
    asset_id: string;
    type: string;
    title: string;
    content: string;
    result?: string;
    created_by: string;
    author_name?: string;
    created_at: string;
    edited_at?: string;
    updated_at: string;
}


const accountApi = axios.create();

const authBaseUrl = process.env.REACT_APP_ACCOUNT_API_URL || `https://api.auth.galactic-group.nl/v1`;
const servicesUrl = process.env.REACT_APP_STATUS_API_URL || `https://api.status.galactic-group.nl/v1`;

export function getCustAuthTotpActive(email: string, ): Promise<boolean> {
    return accountApi.post(`${authBaseUrl}/mfa`, { email })
        .then(response => response.data.totp);
}

export function loginCustAuth(email: string, password: string, extra: {} = {}, mfa: {} = {}): Promise<boolean> {
    return accountApi.post(`${authBaseUrl}/authenticate`, { email, password, extra, mfa })
        .then(response => response.data.token);
}

export function requestCustomerPasswordReset(
    email: string,
    frontendUrl: string,
): Promise<{ accepted: boolean }> {
    return accountApi.post(`${authBaseUrl}/password/reset`, {
        email,
        frontend_url: frontendUrl,
    }).then(response => response.data);
}

export function resetCustomerPassword(
    token: string,
    newPassword: string,
): Promise<{ changed: boolean }> {
    return accountApi.put(`${authBaseUrl}/password/reset`, {
        token,
        new_password: newPassword,
    }).then(response => response.data);
}

export function checkMFA(): Promise<string> {
    return accountApi.post(`${authBaseUrl}/mfa/setup`)
        .then(response => response.data);
}

export function checkEmail(email: string): Promise<{email: string, error:number}> {
    return accountApi.post(`${authBaseUrl}/register/email`, {email}).then(resp => resp.data)
}

export function activateMFA(mfa: {} = {}): Promise<boolean> {
    return accountApi.post(`${authBaseUrl}/mfa/activate`, { mfa })
        .then(response => response.data);
}

export function deactivateMFA(password: string, mfa: {} = {}): Promise<boolean> {
    return accountApi.post(`${authBaseUrl}/mfa/deactivate`, { password, mfa })
        .then(response => response.data);
}

export function changeAccountPassword(
    currentPassword: string,
    newPassword: string
): Promise<{ changed: boolean }> {
    return accountApi.put(`${authBaseUrl}/password`, {
        current_password: currentPassword,
        new_password: newPassword,
    }).then(response => response.data);
}

export function registerCustAuth(
    email: string,
    password: string,
    challenge: Challenge,
    first_name: string,
    last_name: string,
    address: {
        street:string,
        number:string,
        zip_code:string,
        city:string,
        country:string,
    },
    is_business: boolean,
    business_name: string,
    vat_number: string
): Promise<boolean> {
    return accountApi.post(`${authBaseUrl}/register`, {
        email, password, challenge, first_name, last_name,
        address: {
            street: address.street,
            number: address.number,
            postal_code: address.zip_code,
            city: address.city,
            country: address.country,
        },
        is_business, business_name, vat_number })
        .then(response => response.data.success);
}

export function getChallenge(): Promise<{ challenge: number, signature: string }> {
    return accountApi.get(`${authBaseUrl}/challenge`)
        .then(response => response.data);
}

export function getAuthAccounts(): Promise<Account[]> {
    return accountApi.get(`${authBaseUrl}/accounts`)
        .then(response => Array.isArray(response.data) ? response.data as Account[] : []);
}

export function getAccountAssets(accountId: string): Promise<AccountAsset[]> {
    return accountApi.get(`${authBaseUrl}/accounts/${accountId}/assets`)
        .then(response => Array.isArray(response.data) ? response.data as AccountAsset[] : []);
}

export function getAccountAssetNotes(accountId: string, assetId: string): Promise<AccountAssetNote[]> {
    return accountApi.get(`${authBaseUrl}/accounts/${accountId}/assets/${assetId}/notes`)
        .then(response => Array.isArray(response.data) ? response.data as AccountAssetNote[] : []);
}

export function getAccountWebPushConfig(): Promise<{ public_key: string }> {
    return accountApi.get(`${authBaseUrl}/webpush/config`)
        .then(response => response.data);
}

export function subscribeAccountWebPush(
    accountId: string,
    subscription: WebPushSubscriptionPayload
): Promise<{ subscriptions: number }> {
    return accountApi.post(`${authBaseUrl}/accounts/${accountId}/webpush/subscriptions`, subscription)
        .then(response => response.data);
}

export function unsubscribeAccountWebPush(
    accountId: string,
    endpoint: string,
    clientId: string
): Promise<{ subscriptions: number }> {
    return accountApi.delete(`${authBaseUrl}/accounts/${accountId}/webpush/subscriptions`, {
        data: { endpoint, clientId }
    }).then(response => response.data);
}

export function editCustomerVat(id: string, vat: string): Promise<Account> {
    return accountApi.post(`${authBaseUrl}/customers/` + id + `/vat`, { vat_number: vat })
        .then(response => response.data as Account);
}

export function editCustomerIban(id: string, iban: string): Promise<Account> {
    return accountApi.put(`${authBaseUrl}/accounts/${id}/iban`, { iban })
        .then(response => response.data as Account);
}

export function vatCheck(vatNumber: string): Promise<any> {
    return accountApi.get(`${authBaseUrl}/accounts/vat_check/${vatNumber}`)
        .then(response => response.data);
}

export function getPersonLoggedIn(): Promise<Person> {
    return accountApi.get(`${authBaseUrl}/person`)
        .then(response => plainToClass(Person, response.data));
}

export function getAccountLocale(locale: string): Promise<LocaleCatalogue> {
    return accountApi.get(`${authBaseUrl}/locales/${encodeURIComponent(locale)}`)
        .then(response => response.data as LocaleCatalogue);
}

export function getInvoiceExport(id: string): Promise<string> {
    return accountApi.get(`${authBaseUrl}/invoices/${id}/export/pdf`)
        .then(response => response.data.pdf);
}

export function getCustomerPersons(id: string): Promise<Person[]> {
    return accountApi.get(`${authBaseUrl}/customers/${id}/persons`)
        .then(response => response.data.map((person: object): Person => plainToClass(Person, person)));
}

export function updatePerson(person: Person): Promise<Person> {
    return accountApi.put(`${authBaseUrl}/persons/${person.id}`, person)
        .then((response): Person => plainToClass(Person, response.data));
}

export function getInvoices(): Promise<Invoice[]> {
    return accountApi.get(`${authBaseUrl}/invoices`)
        .then(response => (Array.isArray(response.data) ? response.data : [])
            .map((invoice: object): Invoice => plainToClass(Invoice, invoice)));
}

export interface InvoicePaymentFile {
    filename: string;
    xml: string;
    payment_count: number;
    control_sum: number;
}

export function getOpenInvoicePaymentFile(accountId: string): Promise<InvoicePaymentFile> {
    return accountApi.get(`${authBaseUrl}/accounts/${accountId}/invoices/payment-file`)
        .then(response => response.data as InvoicePaymentFile);
}

export function getServices(): Promise<Service[]> {
    return accountApi.get(`${servicesUrl}/services`)
        .then(response => response.data.map((service: object): Service => plainToClass(Service, service)));
}

const selectedAccountParams = (accountId: string) => ({ params: { account_id: accountId } });

export function getWorkerProjects(accountId: string): Promise<Project[]> {
    return accountApi.get(`${authBaseUrl}/projects`, selectedAccountParams(accountId))
        .then(response => response.data);
}

export function getProjectContracts(projectId: string, accountId: string): Promise<ProjectContract[]> {
    return accountApi.get(`${authBaseUrl}/projects/${projectId}/contracts`, selectedAccountParams(accountId))
        .then(response => response.data);
}

export function getProjectContract(projectId: string, contractId: string, accountId: string): Promise<ProjectContract> {
    return accountApi.get(
        `${authBaseUrl}/projects/${projectId}/contracts/${contractId}`,
        selectedAccountParams(accountId)
    )
        .then(response => response.data);
}

export function getProjectContractContentUrl(projectId: string, contractId: string, accountId: string): string {
    const parameters = new URLSearchParams({ account_id: accountId });
    return `${authBaseUrl}/projects/${projectId}/contracts/${contractId}/content?${parameters.toString()}`;
}

export function getProjectContractContent(projectId: string, contractId: string, accountId: string): Promise<Blob> {
    return accountApi.get(
        `${authBaseUrl}/projects/${projectId}/contracts/${contractId}/content`,
        { ...selectedAccountParams(accountId), responseType: "blob" }
    ).then(response => response.data);
}

export function signProjectContract(
    projectId: string,
    contractId: string,
    accountId: string,
    signatureData: string
): Promise<ProjectContract> {
    return accountApi.post(`${authBaseUrl}/projects/${projectId}/contracts/${contractId}/sign`, {
        signature_data: signatureData,
        consent: true
    }, selectedAccountParams(accountId)).then(response => response.data);
}

export function getAccountAttachmentForm(
    accountId: string,
    requirementId: string,
): Promise<AccountAttachmentFormContext> {
    return accountApi.get(`${authBaseUrl}/accounts/${accountId}/attachment-forms/${requirementId}`)
        .then(response => response.data as AccountAttachmentFormContext);
}

export function submitAccountAttachmentForm(
    accountId: string,
    requirementId: string,
    values: {
        declaration_values: boolean[];
        validity_consent: boolean;
        truth_consent: boolean;
        signature_data: string;
    },
): Promise<AccountAttachment> {
    return accountApi.post(
        `${authBaseUrl}/accounts/${accountId}/attachment-forms/${requirementId}`,
        values,
    ).then(response => response.data as AccountAttachment);
}

export function getProjectHours(projectId: string, accountId: string): Promise<ProjectHours> {
    return accountApi.get(`${authBaseUrl}/projects/${projectId}/hours`, selectedAccountParams(accountId))
        .then(response => response.data);
}

export function createProjectHour(
    projectId: string,
    accountId: string,
    entry: Pick<ProjectHourEntry, "minutes"> & { date: string; description?: string }
): Promise<ProjectHourEntry> {
    return accountApi.post(`${authBaseUrl}/projects/${projectId}/hours`, entry, selectedAccountParams(accountId))
        .then(response => response.data);
}

export function updateProjectHour(
    projectId: string,
    entryId: string,
    accountId: string,
    entry: Pick<ProjectHourEntry, "minutes"> & { date: string; description?: string }
): Promise<ProjectHourEntry> {
    return accountApi.put(
        `${authBaseUrl}/projects/${projectId}/hours/${entryId}`,
        entry,
        selectedAccountParams(accountId)
    )
        .then(response => response.data);
}

export function deleteProjectHour(projectId: string, entryId: string, accountId: string): Promise<void> {
    return accountApi.delete(
        `${authBaseUrl}/projects/${projectId}/hours/${entryId}`,
        selectedAccountParams(accountId)
    ).then(() => undefined);
}

export function approveProjectHour(projectId: string, entryId: string, accountId: string): Promise<ProjectHourEntry> {
    return accountApi.post(
        `${authBaseUrl}/projects/${projectId}/hours/${entryId}/approve`,
        undefined,
        selectedAccountParams(accountId)
    )
        .then(response => response.data);
}

export function rejectProjectHour(
    projectId: string,
    entryId: string,
    accountId: string,
    reason: string
): Promise<ProjectHourEntry> {
    return accountApi.post(
        `${authBaseUrl}/projects/${projectId}/hours/${entryId}/reject`,
        { reason },
        selectedAccountParams(accountId)
    )
        .then(response => response.data);
}

export function approveProjectHourWeek(
    projectId: string,
    accountId: string,
    weekStart: string
): Promise<{ approved: number }> {
    return accountApi.post(
        `${authBaseUrl}/projects/${projectId}/hours/approve-week`,
        { week_start: weekStart },
        selectedAccountParams(accountId)
    )
        .then(response => response.data);
}

export function downloadProjectHourWeekPDF(
    projectId: string,
    accountId: string,
    weekStart: string
): Promise<{ pdf: string; filename: string; pending_count: number }> {
    return accountApi.get(
        `${authBaseUrl}/projects/${projectId}/hours/mandays-pdf`,
        { params: { account_id: accountId, week_start: weekStart } }
    ).then(response => response.data);
}

export function uploadAccountAttachment(
    accountId: string,
    requirementId: string,
    file: { name: string; content: string },
    expiresAt?: string
): Promise<AccountAttachment> {
    return accountApi.post(`${authBaseUrl}/accounts/${accountId}/attachments`, {
        requirement_id: requirementId,
        name: file.name,
        content: file.content,
        expires_at: expiresAt ? `${expiresAt}T23:59:59Z` : undefined,
    }).then(response => response.data);
}

export default accountApi;
